import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import type { Icon as PhosphorIcon } from "@phosphor-icons/react";
import {
  MagnifyingGlass,
  House,
  GraduationCap,
  Gear,
  Plus,
  Sun,
  Moon,
  Keyboard,
  Brain,
  ArrowRight,
} from "@phosphor-icons/react";
import { cn } from "../../lib/cn";
import { useUIStore } from "../../stores/ui";
import { useDocumentStore } from "../../stores/document";

export interface CommandItem {
  id: string;
  label: string;
  description?: string;
  icon: PhosphorIcon;
  section: string;
  keywords?: string[];
  action: () => void;
}

interface CommandPaletteProps {
  extraCommands?: CommandItem[];
}

export function CommandPalette({ extraCommands = [] }: CommandPaletteProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const toggleTheme = useUIStore((s) => s.toggleTheme);
  const theme = useUIStore((s) => s.theme);
  const toggleFocusMode = useUIStore((s) => s.toggleFocusMode);
  const projects = useDocumentStore((s) => s.projects);

  const builtinCommands = useMemo<CommandItem[]>(() => {
    const cmds: CommandItem[] = [
      {
        id: "nav-dashboard",
        label: "Go to Dashboard",
        icon: House,
        section: "Navigation",
        keywords: ["home", "projects"],
        action: () => navigate("/"),
      },
      {
        id: "nav-classes",
        label: "Go to Classes",
        icon: GraduationCap,
        section: "Navigation",
        keywords: ["homework", "courses"],
        action: () => navigate("/classes"),
      },
      {
        id: "nav-settings",
        label: "Go to Settings",
        icon: Gear,
        section: "Navigation",
        keywords: ["preferences", "config"],
        action: () => navigate("/settings"),
      },
      {
        id: "new-project",
        label: "New Project",
        description: "Create a new document project",
        icon: Plus,
        section: "Actions",
        keywords: ["create", "document"],
        action: () => navigate("/?create=true"),
      },
      {
        id: "toggle-theme",
        label: `Switch Theme (current: ${theme})`,
        icon: theme === "dark" ? Sun : Moon,
        section: "Actions",
        keywords: ["dark", "light", "appearance"],
        action: toggleTheme,
      },
      {
        id: "toggle-focus",
        label: "Toggle Focus Mode",
        icon: Brain,
        section: "Actions",
        keywords: ["distraction", "zen"],
        action: toggleFocusMode,
      },
      
      {
        id: "shortcuts",
        label: "Keyboard Shortcuts",
        icon: Keyboard,
        section: "Help",
        keywords: ["hotkeys", "keys"],
        action: () => navigate("/settings"),
      },
    ];

    for (const p of projects.slice(0, 10)) {
      cmds.push({
        id: `project-${p.id}`,
        label: p.name,
        description: "Open project",
        icon: ArrowRight,
        section: "Recent Projects",
        keywords: [p.name.toLowerCase()],
        action: () => navigate(`/project/${p.id}`),
      });
    }

    return cmds;
  }, [navigate, theme, toggleTheme, toggleFocusMode, projects]);

  const allCommands = useMemo(
    () => [...builtinCommands, ...extraCommands],
    [builtinCommands, extraCommands],
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return allCommands;
    const q = query.toLowerCase();
    return allCommands.filter((cmd) => {
      const haystack = [cmd.label, cmd.description, cmd.section, ...(cmd.keywords || [])].join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [allCommands, query]);

  const sections = useMemo(() => {
    const map = new Map<string, CommandItem[]>();
    for (const cmd of filtered) {
      const arr = map.get(cmd.section) || [];
      arr.push(cmd);
      map.set(cmd.section, arr);
    }
    return map;
  }, [filtered]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const execute = useCallback((cmd: CommandItem) => {
    cmd.action();
    setOpen(false);
    setQuery("");
  }, []);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQuery("");
      setSelectedIndex(0);
    }
  }, [open]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, filtered.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (filtered[selectedIndex]) execute(filtered[selectedIndex]);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    },
    [filtered, selectedIndex, execute],
  );

  useEffect(() => {
    if (!listRef.current) return;
    const selected = listRef.current.querySelector("[data-selected=true]");
    selected?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  let flatIndex = 0;

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
            className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />

          {/* Palette */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -8 }}
            transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="fixed top-[20%] left-1/2 -translate-x-1/2 z-[101] w-[520px] max-h-[60vh] flex flex-col rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--bg-surface)] shadow-[var(--shadow-xl)] overflow-hidden"
          >
            {/* Input */}
            <div className="flex items-center gap-2.5 px-4 py-3 border-b border-[var(--border-hairline)]">
              <MagnifyingGlass size={16} className="text-[var(--text-muted)] shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type a command or search..."
                className="flex-1 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]/50 bg-transparent outline-none"
              />
              <kbd className="text-[10px] font-mono text-[var(--text-muted)] bg-[var(--bg-base)] rounded border border-[var(--border-hairline)] px-1.5 py-0.5">
                esc
              </kbd>
            </div>

            {/* Results */}
            <div ref={listRef} className="flex-1 overflow-y-auto py-1.5">
              {filtered.length === 0 && (
                <div className="px-4 py-8 text-center text-[13px] text-[var(--text-muted)]">
                  No results for &ldquo;{query}&rdquo;
                </div>
              )}

              {Array.from(sections.entries()).map(([section, items]) => (
                <div key={section}>
                  <div className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]">
                    {section}
                  </div>
                  {items.map((cmd) => {
                    const isSelected = flatIndex === selectedIndex;
                    const idx = flatIndex;
                    flatIndex++;
                    const Icon = cmd.icon;
                    return (
                      <button
                        key={cmd.id}
                        data-selected={isSelected}
                        onClick={() => execute(cmd)}
                        onMouseEnter={() => setSelectedIndex(idx)}
                        className={cn(
                          "flex items-center gap-3 w-full px-4 py-2 text-left transition-colors",
                          isSelected ? "bg-[var(--bg-surface-hover)]" : "hover:bg-[var(--bg-surface-hover)]/50",
                        )}
                      >
                        <Icon size={16} className={isSelected ? "text-[var(--color-lever-orange)]" : "text-[var(--text-muted)]"} />
                        <div className="flex-1 min-w-0">
                          <div className={cn("text-[13px]", isSelected ? "text-[var(--text-primary)]" : "text-[var(--text-secondary)]")}>
                            {cmd.label}
                          </div>
                          {cmd.description && (
                            <div className="text-[11px] text-[var(--text-muted)] truncate">{cmd.description}</div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
